import React, { useState, useEffect, useCallback } from "react";
import {
  CompanyTemplate,
  IndustryListResponse,
  TemplateCompaniesResponse,
  RegisterTemplateCompaniesRequest,
  RegisterTemplateCompaniesResponse,
} from "../types/companyTemplate";

interface CompanyTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CompanyTemplateModal: React.FC<CompanyTemplateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [industries, setIndustries] = useState<string[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<string>("");
  const [templates, setTemplates] = useState<CompanyTemplate[]>([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2>(1);

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5173";

  // 業界一覧を取得
  const fetchIndustries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/company-templates/industries`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data: IndustryListResponse = await response.json();
        if (data.success && data.industries) {
          setIndustries(data.industries);
        }
      } else {
        setError("業界一覧の取得に失敗しました");
      }
    } catch (err) {
      setError("業界一覧の取得中にエラーが発生しました");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  // テンプレート企業を取得
  const fetchTemplateCompanies = useCallback(
    async (industry: string) => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(
          `${API_BASE_URL}/api/company-templates?industry=${encodeURIComponent(
            industry
          )}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data: TemplateCompaniesResponse = await response.json();
          if (data.success && data.companies) {
            setTemplates(data.companies);
            setStep(2);
          } else {
            setTemplates([]);
          }
        } else {
          setError("テンプレート企業の取得に失敗しました");
          setTemplates([]);
        }
      } catch (err) {
        setError("テンプレート企業の取得中にエラーが発生しました");
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    },
    [API_BASE_URL]
  );

  // 業界選択時の処理
  const handleIndustrySelect = (industry: string) => {
    setSelectedIndustry(industry);
    setSelectedCompanyIds([]);
    fetchTemplateCompanies(industry);
  };

  // チェックボックス選択処理
  const handleCheckboxChange = (companyId: number) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId)
        ? prev.filter((id) => id !== companyId)
        : [...prev, companyId]
    );
  };

  // 全選択/全解除
  const handleSelectAll = () => {
    if (selectedCompanyIds.length === templates.length) {
      setSelectedCompanyIds([]);
    } else {
      setSelectedCompanyIds(templates.map((t) => t.id));
    }
  };

  // 登録処理
  const handleRegister = async () => {
    if (selectedCompanyIds.length === 0) {
      alert("少なくとも1つの企業を選択してください");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const requestData: RegisterTemplateCompaniesRequest = {
        industry: selectedIndustry,
        company_ids: selectedCompanyIds,
      };

      const response = await fetch(
        `${API_BASE_URL}/api/company-templates/register`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        }
      );

      if (response.ok) {
        const data: RegisterTemplateCompaniesResponse = await response.json();
        if (data.success) {
          onSuccess();
          handleClose();
        } else {
          setError("企業の登録に失敗しました");
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || "企業の登録に失敗しました");
      }
    } catch (err) {
      setError("企業の登録中にエラーが発生しました");
    } finally {
      setLoading(false);
    }
  };

  // モーダルを閉じる
  const handleClose = () => {
    setStep(1);
    setSelectedIndustry("");
    setTemplates([]);
    setSelectedCompanyIds([]);
    setError(null);
    onClose();
  };

  // 戻る
  const handleBack = () => {
    setStep(1);
    setTemplates([]);
    setSelectedCompanyIds([]);
    setError(null);
  };

  useEffect(() => {
    if (isOpen) {
      fetchIndustries();
    }
  }, [isOpen, fetchIndustries]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-indigo-600 text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">企業テンプレートから登録</h2>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
              {error}
            </div>
          )}

          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-indigo-600"></div>
              <p className="mt-4 text-gray-600">読み込み中...</p>
            </div>
          )}

          {!loading && step === 1 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Step 1: 業界を選択
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {industries.map((industry) => (
                  <button
                    key={industry}
                    onClick={() => handleIndustrySelect(industry)}
                    className="text-left px-4 py-3 border border-gray-300 rounded-md hover:bg-indigo-50 hover:border-indigo-500 transition-colors"
                  >
                    {industry}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!loading && step === 2 && templates.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Step 2: {selectedIndustry} - 人気企業TOP10
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={handleBack}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    戻る
                  </button>
                  <button
                    onClick={handleSelectAll}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {selectedCompanyIds.length === templates.length
                      ? "全解除"
                      : "全選択"}
                  </button>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        選択
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        企業名
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        平均年収
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        HP
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {templates.map((template) => (
                      <tr
                        key={template.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedCompanyIds.includes(template.id)}
                            onChange={() => handleCheckboxChange(template.id)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {template.name}
                            </div>
                            {template.description && (
                              <div className="text-xs text-gray-500 mt-1">
                                {template.description}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {template.average_salary ? (
                            <span className="text-sm text-gray-900">
                              {template.average_salary}万円
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {template.website ? (
                            <a
                              href={template.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-indigo-600 hover:text-indigo-800"
                            >
                              リンク
                            </a>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 2 && templates.length > 0 && !loading && (
          <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
            <button
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
            >
              キャンセル
            </button>
            <button
              onClick={handleRegister}
              disabled={selectedCompanyIds.length === 0}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              登録 ({selectedCompanyIds.length}社)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyTemplateModal;
